import React, { useState } from "react";
import Input from "@/components/Input/Input";
import Textarea from "@/components/Textarea/Textarea";
import { Button, Modal } from "@mui/material";
import addImageIcon from "@/assets/icons/image-upload.svg";
import addTextIcon from "@/assets/icons/txt-upload.svg";
import { IMedia } from "@/store/models/video.models";
import styles from "./EditVideoModal.module.scss";
import { truncateDescription } from "@/helpers/truncateDescr";
import CustomCheckboxes from "@/components/CheckBoxes/CheckBoxes";

type Props = {
  isOpenEditModal: boolean;
  setIsOpenEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedVideo: IMedia | null;
  setSelectedVideo: React.Dispatch<React.SetStateAction<IMedia | null>>;
  handleEditVideo: (formData: FormData) => Promise<void>;
};

const options = [
  { value: "New on KidNation", label: "New on KidNation" },
  { value: "Only on KidNation", label: "Only on KidNation" },
];

const EditVideoModal = ({
  isOpenEditModal,
  setIsOpenEditModal,
  selectedVideo,
  setSelectedVideo,
  handleEditVideo,
}: Props) => {
  const [imgFile, setImgFile] = useState<string | null>(null);
  const [subtitleFileName, setSubtitleFileName] = useState<string>("");
  const [selectedSections, setSelectedSections] = useState<string[]>(
    selectedVideo?.sections ?? []
  );

  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    fileType: string
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (fileType === "photo") {
        setImgFile(URL.createObjectURL(file));
        setSelectedVideo((prev) => ({
          ...prev!,
          thumbnailFile: file,
        }));
      } else if (fileType === "srt") {
        setSubtitleFileName(file.name);
        setSelectedVideo((prev) => ({
          ...prev!,
          subtitleFile: file,
        }));
      }
    }
  };

  const handleSave = () => {
    if (selectedVideo) {
      const formData = new FormData();
      formData.append("uid", selectedVideo.uid);
      formData.append("title", selectedVideo.title || "");
      formData.append("description", selectedVideo.description || "");

      if (selectedVideo.thumbnailFile) {
        formData.append("thumbnailFile", selectedVideo.thumbnailFile);
      }
      if (selectedVideo.subtitleFile) {
        formData.append("subtitleFile", selectedVideo.subtitleFile);
      }
      if (selectedVideo.url) {
        formData.append("url", selectedVideo.url);
      }
      if (selectedSections && selectedSections.length > 0) {
        selectedSections.forEach((section) => {
          formData.append("sections", section);
        });
      }

      handleEditVideo(formData);
    }
  };

  return (
    <Modal
      aria-labelledby="edit video"
      aria-describedby="for editing video Enter the required data and click save"
      open={isOpenEditModal}
      onClose={() => setIsOpenEditModal(false)}
    >
      <div className={styles.modalContainer}>
        <h3 className={styles.modalTitle}>Edit video</h3>
        <div className={styles.modalContent}>
          <Input
            label="Name"
            value={selectedVideo?.title}
            onChange={(e) =>
              setSelectedVideo((prev) => ({
                ...prev!,
                title: e.target.value,
              }))
            }
          />

          <CustomCheckboxes
            label="Select Sections"
            options={options}
            value={selectedSections}
            onChange={setSelectedSections}
          />

          <Textarea
            label="Description"
            value={selectedVideo ? selectedVideo.description : ""}
            onChange={(e) =>
              setSelectedVideo((prev) => ({
                ...prev!,
                description: e.target.value,
              }))
            }
          />

          <label
            className={styles.modalAddItem}
            htmlFor="thumbnail"
            id="dropcontainer"
          >
            <img className={styles.modalImg} src={addImageIcon} alt="img" />
            <input
              id="thumbnail"
              type="file"
              accept="image/*"
              onChange={(e) => handleFileUpload(e, "photo")}
            />

            <span className={styles.modalPrimaryText}>
              or drag and drop the thumbnail here (*.png)
            </span>
          </label>

          {imgFile && (
            <img
              className={styles.thumbnailPreview}
              src={imgFile}
              alt="Thumbnail Preview"
              width={100}
              height={100}
            />
          )}

          {/* This functionality is not working yet */}

          {/* <p className={styles.modalHelper}>or add a link to the file</p>
          <Input
            label=""
            value=""
            onChange={(e) =>
              setSelectedVideo((prev) => ({
                ...prev!,
                thumbnail: e.target.value,
              }))
            }
            placeholder="Enter the link"
          /> */}

          <label
            className={styles.modalAddItem}
            htmlFor="subtitles"
            id="dropcontainer"
          >
            <img className={styles.modalImg} src={addTextIcon} alt="img" />
            <input
              id="subtitles"
              type="file"
              accept=".srt"
              onChange={(e) => handleFileUpload(e, "srt")}
            />

            <span className={styles.modalPrimaryText}>
              {subtitleFileName
                ? truncateDescription(subtitleFileName, 15)
                : "or drag and drop the file here (*.srt)"}
            </span>
          </label>
          {/* This functionality is not working yet */}
          {/* <p className={styles.modalHelper}>or add a link to the file</p>
          <Input
            label=""
            value={selectedVideo?.subtitleUrl || ""}
            onChange={(e) =>
              setSelectedVideo((prev) => ({
                ...prev!,
                subtitleUrl: e.target.value,
              }))
            }
            placeholder="Enter the link"
          /> */}

          <div className={styles.btns}>
            <Button
              sx={{
                textTransform: "capitalize",
                height: "44px",
                width: "130px",
                backgroundColor: "#008000",
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: "#008000",
                },
              }}
              variant="contained"
              onClick={handleSave}
            >
              Save
            </Button>

            <Button
              variant="outlined"
              sx={{
                textTransform: "capitalize",
                height: "44px",
                width: "130px",
                border: "1px solid #5570F1",
                color: "#5570F1",
                borderRadius: "12px",
              }}
              onClick={() => setIsOpenEditModal(false)}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditVideoModal;
